import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPlugin, togglePlugin, type PluginConfig } from "@/api/plugins";

export function SettingsPluginDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: plugin, isLoading, error } = useQuery<PluginConfig>({
    queryKey: ["plugin", id],
    queryFn: () => getPlugin(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: togglePlugin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plugin", id] }),
  });

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <Link to="/settings/plugins" className="text-xs text-muted-foreground hover:text-foreground">&larr; Back to plugins</Link>
      </header>

      {isLoading && <div className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" /></div>}
      {error && <p className="text-sm text-destructive">Failed to load plugin.</p>}

      {plugin && (
        <>
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-foreground">{plugin.name}</h2>
                <p className="text-sm text-muted-foreground">{plugin.description}</p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={plugin.enabled}
                  onChange={() => toggleMutation.mutate(plugin.pluginId)}
                  className="peer sr-only"
                />
                <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">v{plugin.version}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">ID: {plugin.pluginId}</span>
              {plugin.installPath && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{plugin.installPath}</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}